import { Box, Stack, Typography } from '@mui/material';
import { SignedSafetyControlRecord } from '../../services/reportsApi';

function displayStatus(value: string | null | undefined): string {
  return value
    ? value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : 'Not retained';
}

function formatRate(value: number | null | undefined): string | null {
  return value == null || !Number.isFinite(value) ? null : value.toFixed(2);
}

function RateBars({ record }: { record: SignedSafetyControlRecord }) {
  const comparable = record.signed_body.recheck.comparable === true
    && record.signed_body.result.comparability_met === true;
  const baseline = comparable ? record.signed_body.finding.rate_per_usable_hour : null;
  const recheck = comparable ? record.signed_body.recheck.rate_per_usable_hour : null;
  if (baseline == null || recheck == null) {
    return (
      <Box
        data-testid="lifecycle-comparison-withheld"
        sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(8,27,51,0.15)' }}
      >
        <Typography variant="body2" fontWeight={800}>Comparison withheld</Typography>
        <Typography variant="caption" color="text.secondary">
          No directional conclusion is available for this signed revision.
        </Typography>
      </Box>
    );
  }
  const max = Math.max(baseline, recheck, 0.0001);
  const absolute = record.signed_body.result.absolute_rate_change;
  const descriptive = record.signed_body.result.descriptive_percent_change;
  return (
    <Box sx={{ mt: 2 }} data-testid="lifecycle-rate-bars">
      {[
        ['Baseline', baseline, '#f59e0b'],
        ['Recheck', recheck, '#18bde3'],
      ].map(([label, value, color]) => (
        <Box key={String(label)} sx={{ mb: 1.15 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography variant="caption" fontWeight={800}>{label}</Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
            >
              {Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}/h
            </Typography>
          </Stack>
          <Box sx={{ mt: 0.4, height: 8, bgcolor: 'rgba(8,27,51,0.09)' }}>
            <Box sx={{ width: `${(Number(value) / max) * 100}%`, height: '100%', bgcolor: color }} />
          </Box>
        </Box>
      ))}
      <Typography variant="body2" fontWeight={800} sx={{ mt: 1.5 }}>
        {absolute == null
          ? 'Absolute change not retained'
          : `${Math.abs(absolute).toFixed(2)}/h ${absolute <= 0 ? 'lower' : 'higher'}`}
        {descriptive == null
          ? ''
          : ` · ${Math.abs(descriptive).toFixed(1)}% descriptive ${
            descriptive <= 0 ? 'decrease' : 'increase'
          }`}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Signed per-hour rates only; day and week values are unit conversions, not separate trends.
      </Typography>
    </Box>
  );
}

function persistedText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function operationalFindingSentence(
  peakLocation: string | null,
  peakWindow: string | null,
  findingSummary: string | null,
): string {
  if (peakLocation && peakWindow) {
    return `Review separation controls at ${peakLocation} during ${peakWindow}.`;
  }
  if (peakLocation) {
    return `Review separation controls at ${peakLocation}.`;
  }
  if (peakWindow) {
    return `Review separation controls during ${peakWindow}.`;
  }
  if (findingSummary) return findingSummary;
  return 'Recurring pattern was not retained on this signed revision.';
}

function ObservationBasis({ record }: { record: SignedSafetyControlRecord }) {
  const { finding, observation_funnel: funnel, recheck } = record.signed_body;
  const baselineEpisodes = finding.reviewed_episodes;
  const recheckEpisodes = recheck.reviewed_episodes;
  const measuredEpisodes = baselineEpisodes != null && recheckEpisodes != null
    ? baselineEpisodes + recheckEpisodes
    : null;
  const reviewedObservations = measuredEpisodes
    ?? finding.reviewed_episodes
    ?? funnel?.confirmed_count
    ?? null;
  const peakLocation = persistedText(finding.peak_location);
  const peakWindow = persistedText(finding.peak_window);
  const peakCounts = persistedText(finding.peak_counts);
  const hasPeakFacts = Boolean(peakLocation || peakWindow);
  const showGroupingCriteria = Boolean(peakLocation && peakWindow);
  const patternLabel = [peakLocation, peakWindow].filter(Boolean).join(' · ')
    || 'Peak location and window not retained';
  const findingSentence = operationalFindingSentence(
    peakLocation,
    peakWindow,
    persistedText(finding.summary),
  );
  const showPatternStory = reviewedObservations != null || hasPeakFacts;
  const coverageAvailable = finding.usable_hours != null || recheck.usable_hours != null;
  if (!showPatternStory && !coverageAvailable) return null;

  return (
    <Box data-testid="observation-basis" sx={{ mt: 3 }}>
      <Typography component="h3" variant="h5" fontWeight={800}>How this recurring pattern was built</Typography>
      {showPatternStory ? (
        <Box data-testid="observation-funnel" sx={{ mt: 1.5 }}>
          <Typography
            variant="overline"
            sx={{ color: '#087fa5', letterSpacing: '0.1em', fontWeight: 800 }}
          >
            From reviewed events to one operating pattern
          </Typography>
          <Stack spacing={0} sx={{ mt: 1.25 }}>
            {reviewedObservations != null ? (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'rgba(8,27,51,0.04)',
                  border: '1px solid rgba(8,27,51,0.16)',
                }}
              >
                <Typography sx={{ fontWeight: 800 }}>
                  <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 900, mr: 1 }}>
                    {reviewedObservations.toLocaleString()}
                  </Box>
                  reviewed observations
                </Typography>
                {measuredEpisodes != null
                  && baselineEpisodes != null
                  && recheckEpisodes != null ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {baselineEpisodes.toLocaleString()} baseline + {recheckEpisodes.toLocaleString()} recheck
                  </Typography>
                ) : null}
              </Box>
            ) : null}
            {showGroupingCriteria ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', py: 1, pl: 0.5 }}
              >
                ↓ grouped by same area · time window · visible condition · comparable context
              </Typography>
            ) : (
              <Box sx={{ height: 10 }} />
            )}
            <Box
              sx={{
                p: 2,
                bgcolor: '#142849',
                color: '#fff',
                border: '1px solid #142849',
              }}
            >
              <Typography sx={{ fontWeight: 800 }}>
                <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 900, mr: 1 }}>
                  1
                </Box>
                recurring pattern
                {hasPeakFacts ? ` · ${patternLabel}` : ''}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'rgba(255,255,255,0.78)' }}>
                {peakCounts || (hasPeakFacts
                  ? 'Signed time concentration retained for this revision'
                  : 'Peak location and window were not retained on this signed revision')}
              </Typography>
            </Box>
            <Box sx={{ height: 10 }} />
            <Box
              sx={{
                p: 2,
                bgcolor: 'rgba(8,127,165,0.06)',
                border: '1px solid rgba(8,127,165,0.45)',
              }}
            >
              <Typography sx={{ fontWeight: 800 }}>
                <Box
                  component="span"
                  sx={{ fontFamily: 'monospace', fontWeight: 900, mr: 1, color: '#087fa5' }}
                >
                  1
                </Box>
                operational finding — {findingSentence}
              </Typography>
            </Box>
          </Stack>
          {reviewedObservations != null ? (
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 700 }}>
              One pattern, not {reviewedObservations.toLocaleString()} alerts — reviewed
              observations resolve into a single finding the site can act on.
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 700 }}>
              One pattern, not an alert stream — reviewed observations resolve into a single
              finding the site can act on.
            </Typography>
          )}
        </Box>
      ) : null}
      {coverageAvailable ? (
        <Box
          data-testid="coverage-usable-hours-strip"
          sx={{ mt: 2, p: 2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' }, border: '1px solid rgba(8,27,51,0.18)' }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">Baseline usable time</Typography>
            <Typography fontWeight={900}>{finding.usable_hours?.toFixed(3)}h · {finding.coverage_percent}% coverage</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Recheck usable time</Typography>
            <Typography fontWeight={900}>{recheck.usable_hours?.toFixed(3)}h · {recheck.coverage_percent}% coverage</Typography>
          </Box>
          <Typography variant="body2" fontWeight={800} sx={{ alignSelf: 'center', color: '#087fa5' }}>
            Denominator {recheck.measurement_denominator_verified ? 'verified' : 'not verified'}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

function ClaimBoundaryMatrix({ record }: { record: SignedSafetyControlRecord }) {
  const supported = record.signed_body.result.supports.filter(
    (item): item is string => typeof item === 'string' && Boolean(item.trim()),
  );
  const bounded = record.signed_body.result.does_not_establish.filter(
    (item): item is string => typeof item === 'string' && Boolean(item.trim()),
  );
  if (!supported.length && !bounded.length) return null;
  return (
    <Box
      data-testid="claim-boundary-matrix"
      sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, border: '1px solid rgba(8,27,51,0.18)' }}
    >
      {[
        ['Supported, bounded comparison', supported, 'rgba(8,127,165,0.07)'],
        ['Not established', bounded, 'rgba(245,158,11,0.08)'],
      ].map(([title, items, background]) => (
        <Box key={title as string} sx={{ p: 2.25, bgcolor: background as string }}>
          <Typography component="h3" fontWeight={900}>{title as string}</Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.25 }}>
            {(items as string[]).map((item) => <li key={item}><Typography variant="body2">{item}</Typography></li>)}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function LifecycleStory({ record }: { record: SignedSafetyControlRecord }) {
  const { finding, control, recheck, result } = record.signed_body;
  const operational = finding.operational_observation;
  const findingAnswer = typeof finding.summary === 'string' && finding.summary.trim()
    ? finding.summary.trim()
    : 'The recurring pattern was not retained on this signed revision.';
  const controlSummary = control.description || 'No control was retained.';
  const resultLabel = (typeof result.headline === 'string' && result.headline.trim())
    || (result.label === 'not_comparable'
      ? 'Recheck result: Not comparable'
      : result.label === 'inconclusive'
        ? 'Recheck result: Inconclusive'
        : result.label === 'no_change'
          ? 'Verified result: No material change'
          : result.label === 'increased'
            ? 'Verified result: Recurrence increased'
            : displayStatus(result.label));
  const resultDetail = (typeof result.explanation === 'string' && result.explanation.trim())
    || (recheck.comparable
      ? 'The governed signed result is comparable.'
      : 'The retained windows do not support a directional conclusion.');
  const observedEpisodes = finding.reviewed_episodes
    ?? operational?.confirmed_episodes
    ?? null;
  const observedHours = finding.usable_hours
    ?? operational?.monitored_hours
    ?? null;
  const episodesAreOperational = finding.reviewed_episodes == null
    && operational?.confirmed_episodes != null;
  const hoursAreOperational = finding.usable_hours == null
    && operational?.monitored_hours != null;

  return (
    <Box
      component="section"
      aria-labelledby="lifecycle-story-heading"
      data-testid="lifecycle-story"
    >
      <Typography
        variant="overline"
        sx={{ color: '#087fa5', letterSpacing: '0.16em', fontWeight: 800 }}
      >
        Control lifecycle
      </Typography>
      <Typography
        id="lifecycle-story-heading"
        component="h2"
        sx={{ mt: 0.5, mb: 2.5, fontSize: { xs: '1.8rem', md: '2.6rem' }, fontWeight: 800 }}
      >
        Recurring pattern → Control → Recheck
      </Typography>
      <Box
        data-testid="lifecycle-story-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          borderTop: '1px solid rgba(8,27,51,0.22)',
          borderBottom: '1px solid rgba(8,27,51,0.22)',
        }}
      >
        <Box sx={{ py: 3, pr: { md: 3 }, borderBottom: { xs: '1px solid rgba(8,27,51,0.16)', md: 0 } }}>
          <Typography sx={{ color: '#087fa5', fontWeight: 900, letterSpacing: '0.08em' }}>
            01 Recurring pattern
          </Typography>
          <Typography component="h3" variant="h5" sx={{ mt: 1.25, fontWeight: 800 }}>
            {findingAnswer}
          </Typography>
          <Box
            data-testid={episodesAreOperational || hoursAreOperational ? 'lifecycle-operational-context' : undefined}
            sx={episodesAreOperational || hoursAreOperational ? {
              mt: 2,
              p: 1.5,
              border: '1px solid rgba(8,27,51,0.18)',
              borderRadius: 1,
              bgcolor: 'rgba(248,251,255,0.8)',
            } : { mt: 2 }}
          >
            {episodesAreOperational || hoursAreOperational ? (
              <Typography
                variant="caption"
                sx={{ display: 'block', mb: 1, fontWeight: 800, letterSpacing: '0.08em' }}
              >
                OPERATIONAL CONTEXT · NOT PART OF SIGNED CLAIM
              </Typography>
            ) : null}
            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {episodesAreOperational ? 'Confirmed episodes (operational)' : 'Reviewed episodes'}
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 800 }}>
                  {observedEpisodes ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {hoursAreOperational ? 'Monitored hours (untrusted)' : 'Usable hours'}
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 800 }}>
                  {observedHours ?? '—'}
                </Typography>
              </Box>
            </Stack>
            {episodesAreOperational || hoursAreOperational ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Operational counts only · claimable rate not retained
                {formatRate(finding.rate_per_usable_hour) ? ` · ${formatRate(finding.rate_per_usable_hour)}/h` : ''}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            py: 3,
            px: { md: 3 },
            borderLeft: { md: '1px solid rgba(8,27,51,0.16)' },
            borderRight: { md: '1px solid rgba(8,27,51,0.16)' },
            borderBottom: { xs: '1px solid rgba(8,27,51,0.16)', md: 0 },
          }}
        >
          <Typography sx={{ color: '#087fa5', fontWeight: 900, letterSpacing: '0.08em' }}>
            02 Control
          </Typography>
          <Typography component="h3" variant="h5" sx={{ mt: 1.25, fontWeight: 800 }}>
            {controlSummary}
          </Typography>
          <Typography sx={{ mt: 1.25 }}><strong>Owner:</strong> {control.owner || 'Not retained'}</Typography>
          <Typography sx={{ mt: 0.5 }}>
            <strong>Status:</strong> {displayStatus(control.status)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {control.actions?.length
              ? `${control.actions.length} implementation ${
                control.actions.length === 1 ? 'action' : 'actions'
              } retained.`
              : 'No additional implementation actions were retained.'}
          </Typography>
        </Box>

        <Box sx={{ py: 3, pl: { md: 3 } }}>
          <Typography sx={{ color: '#087fa5', fontWeight: 900, letterSpacing: '0.08em' }}>
            03 Recheck
          </Typography>
          <Typography
            component="h3"
            variant="h5"
            data-testid="signed-recheck-result-summary"
            sx={{ mt: 1.25, fontWeight: 800 }}
          >
            {resultLabel}
          </Typography>
          <Typography sx={{ mt: 1.25, lineHeight: 1.6 }}>{resultDetail}</Typography>
          <RateBars record={record} />
        </Box>
      </Box>
      <ObservationBasis record={record} />
      <ClaimBoundaryMatrix record={record} />
    </Box>
  );
}
